import React, { useEffect, useState } from "react";
import {
  Button,
  Menu,
  MenuItem,
  ListItemButton,
  ListItemText,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Language } from "@mui/icons-material";
import useThemeStore from "@/shared/stores/useThemeStore";
import i18n from "../../i18n";

interface LanguageSelectorProps {
  variant?: "button" | "listItem";
  onCloseDrawer?: () => void;
  compact?: boolean;
}

type I18nAdapter = {
  language?: string;
  on?: (event: string, callback: (lng: string) => void) => void;
  off?: (event: string, callback: (lng: string) => void) => void;
  changeLanguage?: (lng: string) => Promise<unknown> | void;
};

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  variant = "button",
  onCloseDrawer,
  compact = false,
}) => {
  const i18nAdapter = i18n as unknown as I18nAdapter;
  const [languageAnchor, setLanguageAnchor] = useState<null | HTMLElement>(
    null,
  );
  const [currentLanguage, setCurrentLanguage] = useState<string>(
    i18nAdapter.language || "en",
  );

  const { darkMode } = useThemeStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isCompact = compact || isMobile;

  // Sync local state with i18n language changes
  useEffect(() => {
    const handleLanguageChange = (lng: string) => {
      setCurrentLanguage(lng);
    };

    if (typeof i18nAdapter.on === "function") {
      i18nAdapter.on("languageChanged", handleLanguageChange);
    }

    return () => {
      if (typeof i18nAdapter.off === "function") {
        i18nAdapter.off("languageChanged", handleLanguageChange);
      }
    };
  }, [i18nAdapter]);

  // Initialize from localStorage on mount
  useEffect(() => {
    const storedLang =
      typeof window !== "undefined" ? localStorage.getItem("lang") : null;
    if (storedLang && storedLang !== i18nAdapter.language) {
      if (typeof i18nAdapter.changeLanguage === "function") {
        i18nAdapter.changeLanguage(storedLang);
      } else {
        setCurrentLanguage(storedLang);
      }
    }
  }, [i18nAdapter]);

  const handleLanguageClick = (event: React.MouseEvent<HTMLElement>) => {
    setLanguageAnchor(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setLanguageAnchor(null);
  };

  const handleLanguageChange = (lang: string) => {
    localStorage.setItem("lang", lang);
    void i18n.changeLanguage(lang);
    handleLanguageClose();
    if (onCloseDrawer) {
      onCloseDrawer();
    }
    // Reload the page to apply language changes
    window.location.reload();
  };

  // Button variant for desktop
  if (variant === "button") {
    return (
      <>
        <Button
          onClick={handleLanguageClick}
          sx={{
            minWidth: isCompact ? "auto" : "auto",
            width: isCompact ? "auto" : "100px",
            px: isCompact ? 0.5 : 2,
            py: isCompact ? 0 : 1,
            height: isCompact ? "22px" : "auto",
            color: darkMode ? "var(--primary-400)" : "#6366F1",
            fontWeight: isCompact ? 700 : 600,
            textTransform: isCompact ? "uppercase" : "none",
            fontFamily: '"Lexend", sans-serif',
            border: "none",
            borderRadius: isCompact ? 1 : 2,
            boxSizing: "border-box",
            outline: "none",
            position: "relative",
            fontSize: isCompact ? 9 : 14,
            lineHeight: isCompact ? 1 : "normal",
            letterSpacing: isCompact ? "0.5px" : "normal",
            "&:hover": {
              backgroundColor: darkMode ? "var(--primary-1000)" : "#f7ecff",
              border: "none",
            },
            "&:focus": {
              outline: "none",
              border: "none",
            },
            "&:active": {
              outline: "none",
              border: "none",
              transform: "none",
            },
            "&:focus-visible": {
              outline: "none",
              border: "none",
            },
            "& .MuiButton-startIcon": {
              marginRight: isCompact ? 0 : 0.5,
              marginLeft: 0,
            },
          }}
          startIcon={!isCompact ? <Language /> : undefined}
        >
          {isCompact
            ? currentLanguage === "kr"
              ? "KR"
              : currentLanguage === "ja"
                ? "JP"
                : "EN"
            : currentLanguage === "kr"
              ? "한국어"
              : currentLanguage === "ja"
                ? "日本語"
                : "English"}
        </Button>
        <Menu
          anchorEl={languageAnchor}
          open={Boolean(languageAnchor)}
          onClose={handleLanguageClose}
          disableScrollLock={true}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          sx={{
            "& .MuiPaper-root": {
              bgcolor: darkMode ? "var(--neutral-900)" : "var(--neutralWhite)",
              border: darkMode
                ? "1px solid var(--neutral-800)"
                : "1px solid #E5E7EB",
              mt: 0.5,
            },
          }}
        >
          <MenuItem
            onClick={() => handleLanguageChange("en")}
            selected={currentLanguage === "en"}
            sx={{
              color:
                currentLanguage === "en"
                  ? darkMode
                    ? "var(--primary-700)"
                    : "#9F03FF"
                  : darkMode
                    ? "var(--neutralWhite)"
                    : "#111827",
              fontFamily: '"Lexend", sans-serif',
              fontWeight: currentLanguage === "en" ? 600 : 400,
              "&:hover": {
                backgroundColor: darkMode ? "var(--neutral-800)" : "#F9FAFB",
              },
            }}
          >
            English
          </MenuItem>
          <MenuItem
            onClick={() => handleLanguageChange("kr")}
            selected={currentLanguage === "kr"}
            sx={{
              color:
                currentLanguage === "kr"
                  ? darkMode
                    ? "var(--primary-700)"
                    : "#9F03FF"
                  : darkMode
                    ? "var(--neutralWhite)"
                    : "#111827",
              fontFamily: '"Lexend", sans-serif',
              fontWeight: currentLanguage === "kr" ? 600 : 400,
              "&:hover": {
                backgroundColor: darkMode ? "var(--neutral-800)" : "#F9FAFB",
              },
            }}
          >
            한국어
          </MenuItem>
          <MenuItem
            onClick={() => handleLanguageChange("ja")}
            selected={currentLanguage === "ja"}
            sx={{
              color:
                currentLanguage === "ja"
                  ? darkMode
                    ? "var(--primary-700)"
                    : "#9F03FF"
                  : darkMode
                    ? "var(--neutralWhite)"
                    : "#111827",
              fontFamily: '"Lexend", sans-serif',
              fontWeight: currentLanguage === "ja" ? 600 : 400,
              "&:hover": {
                backgroundColor: darkMode ? "var(--neutral-800)" : "#F9FAFB",
              },
            }}
          >
            日本語
          </MenuItem>
        </Menu>
      </>
    );
  }

  // ListItem variant for mobile drawer
  return (
    <ListItemButton
      onClick={() => {
        const newLang =
          currentLanguage === "en"
            ? "kr"
            : currentLanguage === "kr"
              ? "ja"
              : "en";
        handleLanguageChange(newLang);
      }}
    >
      <ListItemText
        primary={
          currentLanguage === "kr"
            ? "Language: 한국어"
            : currentLanguage === "ja"
              ? "Language: 日本語"
              : "Language: English"
        }
      />
    </ListItemButton>
  );
};

export default LanguageSelector;
